import { MicOff } from "@mui/icons-material";
import { colors } from "../../../ui/colors";
import { Box, Button, Typography } from "@mui/material";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";

export type MicPermissionStatus =
    | "idle"
    | "checking"
    | "granted"
    | "denied"
    | "unsupported";


export const MicrophonePermissionEmptyState = ({
    status,
    onRetry,
    compact = false,
}: {
    status: MicPermissionStatus;
    onRetry: () => void;
    compact?: boolean;
}) => {
    const { theme } = useContactCenterTheme();
    const isUnsupported = status === "unsupported";

    return (
        <Box
            sx={{
                minHeight: compact ? "auto" : 420,
                height: compact ? "auto" : "100%",
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                px: compact ? 1.5 : 2,
                pt: compact ? 0 : 2,
                pb: compact ? 1.5 : 2,
                display: "flex",
                alignItems: compact ? "flex-start" : "center",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 370,
                    bgcolor: theme.shell.headerBg,
                    borderRadius: compact ? "18px" : "22px",
                    px: compact ? 2.25 : 3,
                    py: compact ? 2.25 : 4,
                    textAlign: "center",
                    boxShadow: "0 5px 12px rgba(8, 24, 40, 0.08)",
                }}
            >
                <Box
                    sx={{
                        width: compact ? 58 : 76,
                        height: compact ? 58 : 76,
                        mx: "auto",
                        mb: compact ? 1.5 : 2,
                        borderRadius: "50%",
                        bgcolor: colors.orange[50],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid",
                        borderColor: colors.orange[100],
                    }}
                >
                    <MicOff sx={{ fontSize: compact ? 28 : 36, color: colors.orange[600] }} />
                </Box>

                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 900,
                        color: "var(--fx-surface-text, #0F172A)",
                        mb: 0.75,
                    }}
                >
                    {isUnsupported
                        ? "Microphone is not available"
                        : "Microphone access is blocked"}
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        color: "var(--fx-surface-textsecondary, #64748B)",
                        lineHeight: 1.6,
                        mb: compact ? 1.5 : 2.5,
                    }}
                >
                    {isUnsupported
                        ? "Your browser or current connection does not support microphone access. Use HTTPS or localhost and try again."
                        : "We need microphone permission to detect your audio devices and test your input settings."}
                </Typography>

                {!isUnsupported && (
                    <Button
                        fullWidth
                        onClick={onRetry}
                        sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 800,
                            py: 1,
                            bgcolor: colors.orange[500],
                            color: "#fff",
                            boxShadow: `0 2px 6px ${colors.orange[100]}`,
                            "&:hover": {
                                bgcolor: colors.orange[600],
                                boxShadow: `0 2px 6px ${colors.orange[100]}`,
                            },
                        }}
                    >
                        Allow microphone access
                    </Button>
                )}

                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mt: compact ? 1.25 : 2,
                        color: "var(--fx-surface-texttertiary, #94A3B8)",
                        lineHeight: 1.5,
                    }}
                >
                    {isUnsupported
                        ? "Microphone settings require a secure browser context."
                        : "If the browser does not ask again, enable microphone access from your site settings."}
                </Typography>
            </Box>
        </Box>
    );
};
